// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  CHARGE_TRIGGER_TYPE_DESCRIPTORS,
  CHARGE_TRIGGER_TYPE_ORDER,
} from '@/billing/components/charge-trigger-type-icons';
import { FilterMenuButton } from '@/components/filter-menu-button';
import { FilterMenuItem } from '@/components/filter-menu-item';
import { ChargeTriggerType } from '@/constants/charge-trigger-type';
import { useDisclosure } from '@/hooks/use-disclosure';
import { Box, Menu, Stack } from '@mui/material';
import { useRef } from 'react';

interface Props {
  value: ChargeTriggerType[];
  onChange: (next: ChargeTriggerType[]) => void;
}

// Multi-select trigger-type filter. Mirrors FilterMenu's button + menu
// structure but renders each option with its icon so the user learns the
// iconography used in the billable-date cell. Icons are sourced from
// CHARGE_TRIGGER_TYPE_DESCRIPTORS to stay in lockstep with the table row.
export function ChargeTriggerTypeFilterMenu(props: Props) {
  const menu = useDisclosure();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const activeSet = new Set(props.value);

  const toggle = (id: ChargeTriggerType, shouldActivate: boolean) => {
    if (shouldActivate) {
      props.onChange([...props.value, id]);
    } else {
      props.onChange(props.value.filter((t) => t !== id));
    }
  };

  return (
    <>
      <FilterMenuButton
        ref={anchorRef}
        count={activeSet.size}
        isDisabled={false}
        onClick={menu.open}
      >
        Trigger
      </FilterMenuButton>
      <Menu
        open={menu.isOpen}
        anchorEl={anchorRef.current}
        onClose={menu.close}
      >
        {CHARGE_TRIGGER_TYPE_ORDER.map((id) => {
          const descriptor = CHARGE_TRIGGER_TYPE_DESCRIPTORS[id];
          return (
            <FilterMenuItem
              key={id}
              isActive={activeSet.has(id)}
              onChange={(isActive) => toggle(id, isActive)}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: 'text.secondary',
                  }}
                >
                  {descriptor.icon}
                </Box>
                <span>{descriptor.label}</span>
              </Stack>
            </FilterMenuItem>
          );
        })}
      </Menu>
    </>
  );
}
